import { FirebaseApp, initializeApp } from 'firebase/app';
import { Analytics, getAnalytics } from 'firebase/analytics';
import { Firestore, getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { firebaseAppConfig } from 'src/constants/firebaseConstants';
import { logInfoToConsole } from './loggingUtils';

// Singleton instances
let firebaseApp: FirebaseApp | null = null;
let analytics: Analytics | null = null;
let firestore: Firestore | null = null;
let isEmulatorConnected = false;

/**
 * Initializes firebase app.
 * @returns The initialized Firebase app instance.
 */
export const initializeFirebaseApp = (): FirebaseApp => {
    if (!firebaseApp) {
        firebaseApp = initializeApp(firebaseAppConfig);
    }
    return firebaseApp;
};

/**
 * Initializes Firebase analytics.
 * @param app - Optional Firebase app instance to use.
 * @returns The Firebase analytics instance.
 */
export const initializeFirebaseAnalytics = (app?: FirebaseApp) => {
    if (!analytics) {
        const appInstance = app || initializeFirebaseApp();
        analytics = getAnalytics(appInstance);
    }
    return analytics;
};

/**
 * Initializes Firebase Firestore.
 * @param app - Optional Firebase app instance to use.
 * @returns The Firebase Firestore instance.
 */
export const initializeFirestore = (app?: FirebaseApp) => {
    if (!firestore) {
        const appInstance = app || initializeFirebaseApp();
        firestore = getFirestore(appInstance);

        // Connect to emulator in development (only once)
        if (import.meta.env.DEV && !isEmulatorConnected) {
            const host = import.meta.env.VITE_FIRESTORE_EMULATOR_HOST || 'localhost';
            const port = parseInt(import.meta.env.VITE_FIRESTORE_EMULATOR_PORT || '8080', 10);
            connectFirestoreEmulator(firestore, host, port);
            isEmulatorConnected = true;
            logInfoToConsole(`🔥 Firestore emulator connected at ${host}:${port}`);
        }
    }

    return firestore;
};
